import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react'
import { customerService, vehicleService, workEntryService, paymentService, paymentOfferService, driverService, serviceService, fuelService, rentalService, cacheUtils } from '../services/crudService'

// =============================================================================
// CONTEXT SETUP
// =============================================================================

const DataContext = createContext()

// =============================================================================
// ACTIONS
// =============================================================================

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CUSTOMERS: 'SET_CUSTOMERS',
  ADD_CUSTOMER: 'ADD_CUSTOMER',
  UPDATE_CUSTOMER: 'UPDATE_CUSTOMER',
  DELETE_CUSTOMER: 'DELETE_CUSTOMER',
  SET_VEHICLES: 'SET_VEHICLES',
  ADD_VEHICLE: 'ADD_VEHICLE',
  UPDATE_VEHICLE: 'UPDATE_VEHICLE',
  DELETE_VEHICLE: 'DELETE_VEHICLE',
  ASSIGN_VEHICLE: 'ASSIGN_VEHICLE',
  UNASSIGN_VEHICLE: 'UNASSIGN_VEHICLE',
  SET_WORK_ENTRIES: 'SET_WORK_ENTRIES',
  ADD_WORK_ENTRY: 'ADD_WORK_ENTRY',
  UPDATE_WORK_ENTRY: 'UPDATE_WORK_ENTRY',
  DELETE_WORK_ENTRY: 'DELETE_WORK_ENTRY',
  SET_DRIVERS: 'SET_DRIVERS',
  ADD_DRIVER: 'ADD_DRIVER',
  UPDATE_DRIVER: 'UPDATE_DRIVER',
  DELETE_DRIVER: 'DELETE_DRIVER',
  SET_SERVICES: 'SET_SERVICES',
  ADD_SERVICE: 'ADD_SERVICE',
  UPDATE_SERVICE: 'UPDATE_SERVICE',
  DELETE_SERVICE: 'DELETE_SERVICE',
  SET_FUEL_ENTRIES: 'SET_FUEL_ENTRIES',
  ADD_FUEL_ENTRY: 'ADD_FUEL_ENTRY',
  UPDATE_FUEL_ENTRY: 'UPDATE_FUEL_ENTRY',
  DELETE_FUEL_ENTRY: 'DELETE_FUEL_ENTRY',
  SET_RENTALS: 'SET_RENTALS',
  ADD_RENTAL: 'ADD_RENTAL',
  UPDATE_RENTAL: 'UPDATE_RENTAL',
  DELETE_RENTAL: 'DELETE_RENTAL',
  ADD_PAYMENT: 'ADD_PAYMENT',
  ADD_PAYMENT_OFFER: 'ADD_PAYMENT_OFFER',
  REFRESH_ALL_DATA: 'REFRESH_ALL_DATA',
  CLEAR_ERROR: 'CLEAR_ERROR'
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState = {
  customers: [],
  vehicles: [],
  workEntries: [],
  drivers: [],
  services: [],
  fuelEntries: [],
  rentals: [],
  loading: false,
  error: null,
  lastUpdated: null
}

// =============================================================================
// REDUCER
// =============================================================================

function dataReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload }
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false }
    
    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null }
    
    case ACTIONS.SET_CUSTOMERS:
      return { 
        ...state, 
        customers: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_CUSTOMER:
      return { 
        ...state, 
        customers: [action.payload, ...state.customers],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_CUSTOMER:
      return { 
        ...state, 
        customers: state.customers.map(c => 
          c.id === action.payload.id ? action.payload : c
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_CUSTOMER:
      return { 
        ...state, 
        customers: state.customers.filter(c => c.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_VEHICLES:
      return { 
        ...state, 
        vehicles: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_VEHICLE:
      return { 
        ...state, 
        vehicles: [action.payload, ...state.vehicles],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_VEHICLE:
      return { 
        ...state, 
        vehicles: state.vehicles.map(v => 
          v.id === action.payload.id ? action.payload : v
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_VEHICLE:
      return { 
        ...state, 
        vehicles: state.vehicles.filter(v => v.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ASSIGN_VEHICLE:
      return { 
        ...state, 
        vehicles: state.vehicles.map(v => 
          v.id === action.payload.vehicleId 
            ? { ...v, is_assigned: true, assigned_to: action.payload.customerId }
            : v
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UNASSIGN_VEHICLE:
      return { 
        ...state, 
        vehicles: state.vehicles.map(v => 
          v.id === action.payload 
            ? { ...v, is_assigned: false, assigned_to: null }
            : v
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_WORK_ENTRIES:
      return { 
        ...state, 
        workEntries: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_WORK_ENTRY:
      return { 
        ...state, 
        workEntries: [action.payload, ...state.workEntries],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_WORK_ENTRY:
      return { 
        ...state, 
        workEntries: state.workEntries.map(w => 
          w.id === action.payload.id ? action.payload : w
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_WORK_ENTRY:
      return { 
        ...state, 
        workEntries: state.workEntries.filter(w => w.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_DRIVERS:
      return { 
        ...state, 
        drivers: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_DRIVER:
      return { 
        ...state, 
        drivers: [action.payload, ...state.drivers],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_DRIVER:
      return { 
        ...state, 
        drivers: state.drivers.map(d => 
          d.id === action.payload.id ? action.payload : d
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_DRIVER:
      return { 
        ...state, 
        drivers: state.drivers.filter(d => d.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_SERVICES:
      return { 
        ...state, 
        services: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_SERVICE:
      return { 
        ...state, 
        services: [action.payload, ...state.services],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_SERVICE:
      return { 
        ...state, 
        services: state.services.map(s => 
          s.id === action.payload.id ? action.payload : s
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_SERVICE:
      return { 
        ...state, 
        services: state.services.filter(s => s.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_FUEL_ENTRIES:
      return { 
        ...state, 
        fuelEntries: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_FUEL_ENTRY:
      return { 
        ...state, 
        fuelEntries: [action.payload, ...state.fuelEntries],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_FUEL_ENTRY:
      return { 
        ...state, 
        fuelEntries: state.fuelEntries.map(f => 
          f.id === action.payload.id ? action.payload : f
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_FUEL_ENTRY:
      return { 
        ...state, 
        fuelEntries: state.fuelEntries.filter(f => f.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.SET_RENTALS:
      return { 
        ...state, 
        rentals: action.payload,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_RENTAL:
      return { 
        ...state, 
        rentals: [action.payload, ...state.rentals],
        lastUpdated: Date.now()
      }
    
    case ACTIONS.UPDATE_RENTAL:
      return { 
        ...state, 
        rentals: state.rentals.map(r => 
          r.id === action.payload.id ? action.payload : r
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.DELETE_RENTAL:
      return { 
        ...state, 
        rentals: state.rentals.filter(r => r.id !== action.payload),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.REFRESH_ALL_DATA:
      return { 
        ...state, 
        customers: action.payload.customers || state.customers,
        vehicles: action.payload.vehicles || state.vehicles,
        workEntries: action.payload.workEntries || state.workEntries,
        drivers: action.payload.drivers || state.drivers,
        services: action.payload.services || state.services,
        fuelEntries: action.payload.fuelEntries || state.fuelEntries,
        rentals: action.payload.rentals || state.rentals,
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_PAYMENT:
      return { 
        ...state, 
        workEntries: state.workEntries.map(w => 
          w.id === action.payload.workEntryId 
            ? { 
                ...w, 
                paid_amount: (w.paid_amount || 0) + parseFloat(action.payload.payment.amount),
                remaining_amount: Math.max(0, ((w.amount || 0) + (w.tax_amount || 0)) - ((w.paid_amount || 0) + parseFloat(action.payload.payment.amount))),
                payment_status: ((w.paid_amount || 0) + parseFloat(action.payload.payment.amount)) >= ((w.amount || 0) + (w.tax_amount || 0)) ? 'paid' : 'partial'
              }
            : w
        ),
        lastUpdated: Date.now()
      }
    
    case ACTIONS.ADD_PAYMENT_OFFER:
      return { 
        ...state, 
        workEntries: state.workEntries.map(w => 
          w.id === action.payload.workEntryId 
            ? { ...w, agreed_amount: action.payload.offer.amount }
            : w
        ),
        lastUpdated: Date.now()
      }
    
    default:
      return state
  }
}

// =============================================================================
// DATA PROVIDER COMPONENT
// =============================================================================

export function DataProvider({ children }) {
  const [state, dispatch] = useReducer(dataReducer, initialState)

  // Memoized selectors for better performance
  const memoizedState = useMemo(() => ({
    ...state,
    // Pre-computed statistics
    stats: {
      totalCustomers: state.customers.length,
      totalVehicles: state.vehicles.length,
      totalWorkEntries: state.workEntries.length,
      activeVehicles: state.vehicles.filter(v => v.status === 'active').length,
      pendingPayments: state.workEntries.filter(w => w.payment_status === 'pending').length
    }
  }), [state])

  // Load initial data with caching
  const loadInitialData = useCallback(async (forceRefresh = false) => {
    // Check if data is fresh (less than 5 minutes old)
    const isDataFresh = state.lastUpdated && (Date.now() - state.lastUpdated) < 5 * 60 * 1000
    
    if (!forceRefresh && isDataFresh && state.customers.length > 0) {
      console.log('Using cached data')
      return
    }

    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Loading timeout. Please refresh the page.' })
    }, 30000) // 30 seconds timeout

    try {
      // Load data in parallel for better performance
      const [customersResult, vehiclesResult, workEntriesResult, driversResult, servicesResult, fuelEntriesResult, rentalsResult] = await Promise.all([
        customerService.getAll(),
        vehicleService.getAll(),
        workEntryService.getAll(),
        driverService.getAll(),
        serviceService.getAll(),
        fuelService.getAll(),
        rentalService.getAll()
      ])

      clearTimeout(timeoutId) // Clear timeout if successful

      if (customersResult.success) {
        dispatch({ type: ACTIONS.SET_CUSTOMERS, payload: customersResult.data })
      }

      if (vehiclesResult.success) {
        dispatch({ type: ACTIONS.SET_VEHICLES, payload: vehiclesResult.data })
      }

      if (workEntriesResult.success) {
        dispatch({ type: ACTIONS.SET_WORK_ENTRIES, payload: workEntriesResult.data })
      }

      if (driversResult.success) {
        dispatch({ type: ACTIONS.SET_DRIVERS, payload: driversResult.data })
      }

      if (servicesResult.success) {
        dispatch({ type: ACTIONS.SET_SERVICES, payload: servicesResult.data })
      }

      if (fuelEntriesResult.success) {
        dispatch({ type: ACTIONS.SET_FUEL_ENTRIES, payload: fuelEntriesResult.data })
      }

      if (rentalsResult.success) {
        dispatch({ type: ACTIONS.SET_RENTALS, payload: rentalsResult.data })
      }
    } catch (error) {
      clearTimeout(timeoutId) // Clear timeout on error
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.lastUpdated])

  // Function to refresh all data
  const refreshAllData = useCallback(async () => {
    try {
      console.log('Starting refreshAllData...')
      
      // Add a small delay to ensure database operations are complete
      await new Promise(resolve => setTimeout(resolve, 100))
      
      const [customersResult, vehiclesResult, workEntriesResult, driversResult, servicesResult, fuelEntriesResult, rentalsResult] = await Promise.all([
        customerService.getAll(true), // Force refresh
        vehicleService.getAll(true), // Force refresh
        workEntryService.getAll(true), // Force refresh
        driverService.getAll(true), // Force refresh
        serviceService.getAll(true), // Force refresh
        fuelService.getAll(true), // Force refresh
        rentalService.getAll(true) // Force refresh
      ])

      console.log('Refresh results:', {
        customers: customersResult.success ? customersResult.data.length : 'failed',
        vehicles: vehiclesResult.success ? vehiclesResult.data.length : 'failed',
        workEntries: workEntriesResult.success ? workEntriesResult.data.length : 'failed',
        drivers: driversResult.success ? driversResult.data.length : 'failed',
        services: servicesResult.success ? servicesResult.data.length : 'failed',
        fuelEntries: fuelEntriesResult.success ? fuelEntriesResult.data.length : 'failed',
        rentals: rentalsResult.success ? rentalsResult.data.length : 'failed'
      })

      const refreshData = {}
      if (customersResult.success) {
        refreshData.customers = customersResult.data
      }
      if (vehiclesResult.success) {
        refreshData.vehicles = vehiclesResult.data
      }
      if (workEntriesResult.success) {
        refreshData.workEntries = workEntriesResult.data
      }
      if (driversResult.success) {
        refreshData.drivers = driversResult.data
      }
      if (servicesResult.success) {
        refreshData.services = servicesResult.data
      }
      if (fuelEntriesResult.success) {
        refreshData.fuelEntries = fuelEntriesResult.data
      }
      if (rentalsResult.success) {
        refreshData.rentals = rentalsResult.data
      }

      console.log('Dispatching REFRESH_ALL_DATA with:', Object.keys(refreshData))
      dispatch({ type: ACTIONS.REFRESH_ALL_DATA, payload: refreshData })
      console.log('refreshAllData completed successfully')
    } catch (error) {
      console.error('Error refreshing data:', error)
      // Retry once on error
      try {
        console.log('Retrying refreshAllData...')
        await new Promise(resolve => setTimeout(resolve, 500))
        const [customersResult, vehiclesResult, workEntriesResult, driversResult, servicesResult, fuelEntriesResult, rentalsResult] = await Promise.all([
          customerService.getAll(true),
          vehicleService.getAll(true),
          workEntryService.getAll(true),
          driverService.getAll(true),
          serviceService.getAll(true),
          fuelService.getAll(true),
          rentalService.getAll(true)
        ])
        
        const refreshData = {}
        if (customersResult.success) refreshData.customers = customersResult.data
        if (vehiclesResult.success) refreshData.vehicles = vehiclesResult.data
        if (workEntriesResult.success) refreshData.workEntries = workEntriesResult.data
        if (driversResult.success) refreshData.drivers = driversResult.data
        if (servicesResult.success) refreshData.services = servicesResult.data
        if (fuelEntriesResult.success) refreshData.fuelEntries = fuelEntriesResult.data
        if (rentalsResult.success) refreshData.rentals = rentalsResult.data
        
        dispatch({ type: ACTIONS.REFRESH_ALL_DATA, payload: refreshData })
        console.log('refreshAllData retry completed successfully')
      } catch (retryError) {
        console.error('Error in refreshAllData retry:', retryError)
      }
    }
  }, [])

  // Load data on mount
  useEffect(() => {
    loadInitialData()
  }, []) // Empty dependency array to run only once on mount

  // Optimized customer actions with optimistic updates
  const addCustomer = useCallback(async (customerData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    // Optimistic update
    const optimisticCustomer = {
      id: `temp-${Date.now()}`,
      ...customerData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    dispatch({ type: ACTIONS.ADD_CUSTOMER, payload: optimisticCustomer })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await customerService.create(customerData)
      if (result.success) {
        // Remove optimistic update and add real data
        dispatch({ type: ACTIONS.DELETE_CUSTOMER, payload: optimisticCustomer.id })
        dispatch({ type: ACTIONS.ADD_CUSTOMER, payload: result.data })
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.DELETE_CUSTOMER, payload: optimisticCustomer.id })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.DELETE_CUSTOMER, payload: optimisticCustomer.id })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateCustomer = useCallback(async (id, customerData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    // Optimistic update
    const optimisticCustomer = { id, ...customerData, updated_at: new Date().toISOString() }
    const originalCustomer = state.customers.find(c => c.id === id)
    
    dispatch({ type: ACTIONS.UPDATE_CUSTOMER, payload: optimisticCustomer })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await customerService.update(id, customerData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_CUSTOMER, payload: result.data })
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.UPDATE_CUSTOMER, payload: originalCustomer })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.UPDATE_CUSTOMER, payload: originalCustomer })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.customers])

  const deleteCustomer = useCallback(async (id) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    // Optimistic update
    const originalCustomer = state.customers.find(c => c.id === id)
    dispatch({ type: ACTIONS.DELETE_CUSTOMER, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await customerService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.ADD_CUSTOMER, payload: originalCustomer })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.ADD_CUSTOMER, payload: originalCustomer })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.customers])

  // Optimized vehicle actions
  const addVehicle = useCallback(async (vehicleData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticVehicle = {
      id: `temp-${Date.now()}`,
      ...vehicleData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    dispatch({ type: ACTIONS.ADD_VEHICLE, payload: optimisticVehicle })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await vehicleService.create(vehicleData)
      if (result.success) {
        // Remove optimistic update and add real data
        dispatch({ type: ACTIONS.DELETE_VEHICLE, payload: optimisticVehicle.id })
        dispatch({ type: ACTIONS.ADD_VEHICLE, payload: result.data })
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.DELETE_VEHICLE, payload: optimisticVehicle.id })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.DELETE_VEHICLE, payload: optimisticVehicle.id })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateVehicle = useCallback(async (id, vehicleData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticVehicle = { id, ...vehicleData, updated_at: new Date().toISOString() }
    const originalVehicle = state.vehicles.find(v => v.id === id)
    
    dispatch({ type: ACTIONS.UPDATE_VEHICLE, payload: optimisticVehicle })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await vehicleService.update(id, vehicleData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_VEHICLE, payload: result.data })
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_VEHICLE, payload: originalVehicle })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_VEHICLE, payload: originalVehicle })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.vehicles])

  const deleteVehicle = useCallback(async (id) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const originalVehicle = state.vehicles.find(v => v.id === id)
    dispatch({ type: ACTIONS.DELETE_VEHICLE, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await vehicleService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.ADD_VEHICLE, payload: originalVehicle })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.ADD_VEHICLE, payload: originalVehicle })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.vehicles])

  const assignVehicle = useCallback(async (vehicleId, customerId) => {
    dispatch({ type: ACTIONS.ASSIGN_VEHICLE, payload: { vehicleId, customerId } })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await vehicleService.assignToCustomer(vehicleId, customerId)
      if (result.success) {
        // Refresh all data after successful assignment
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.UNASSIGN_VEHICLE, payload: vehicleId })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UNASSIGN_VEHICLE, payload: vehicleId })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    }
  }, [])

  const unassignVehicle = useCallback(async (vehicleId) => {
    dispatch({ type: ACTIONS.UNASSIGN_VEHICLE, payload: vehicleId })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await vehicleService.unassignFromCustomer(vehicleId)
      if (result.success) {
        // Refresh all data after successful unassignment
        await refreshAllData()
        return { success: true }
      } else {
        // Rollback would need original assignment data
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    }
  }, [])

  // Optimized work entry actions
  const addWorkEntry = useCallback(async (workEntryData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticEntry = {
      id: `temp-${Date.now()}`,
      ...workEntryData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    dispatch({ type: ACTIONS.ADD_WORK_ENTRY, payload: optimisticEntry })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await workEntryService.create(workEntryData)
      if (result.success) {
        // Remove optimistic update and add real data
        dispatch({ type: ACTIONS.DELETE_WORK_ENTRY, payload: optimisticEntry.id })
        dispatch({ type: ACTIONS.ADD_WORK_ENTRY, payload: result.data })
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.DELETE_WORK_ENTRY, payload: optimisticEntry.id })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.DELETE_WORK_ENTRY, payload: optimisticEntry.id })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateWorkEntry = useCallback(async (id, workEntryData) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticEntry = { id, ...workEntryData, updated_at: new Date().toISOString() }
    const originalEntry = state.workEntries.find(w => w.id === id)
    
    dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: optimisticEntry })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await workEntryService.update(id, workEntryData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: result.data })
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: originalEntry })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: originalEntry })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.workEntries])

  const deleteWorkEntry = useCallback(async (id) => {
    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const originalEntry = state.workEntries.find(w => w.id === id)
    dispatch({ type: ACTIONS.DELETE_WORK_ENTRY, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await workEntryService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.ADD_WORK_ENTRY, payload: originalEntry })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.ADD_WORK_ENTRY, payload: originalEntry })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.workEntries])

  // Driver actions
  const addDriver = useCallback(async (driverData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await driverService.create(driverData)
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_DRIVER, payload: result.data })
        // Clear driver cache to ensure fresh data
        cacheUtils.clearDrivers()
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateDriver = useCallback(async (id, driverData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    const originalDriver = state.drivers.find(d => d.id === id)
    const optimisticDriver = { ...originalDriver, ...driverData }
    
    dispatch({ type: ACTIONS.UPDATE_DRIVER, payload: optimisticDriver })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      console.log('Updating driver:', id, driverData)
      const result = await driverService.update(id, driverData)
      console.log('Driver update result:', result)
      
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_DRIVER, payload: result.data })
        console.log('Driver updated successfully, clearing cache and refreshing all data...')
        // Clear driver cache to ensure fresh data
        cacheUtils.clearDrivers()
        // Refresh all data after successful update
        await refreshAllData()
        console.log('All data refreshed after driver update')
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_DRIVER, payload: originalDriver })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        console.log('Driver update failed:', result.error)
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_DRIVER, payload: originalDriver })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      console.log('Driver update error:', error.message)
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.drivers])

  const deleteDriver = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    const originalDriver = state.drivers.find(d => d.id === id)
    dispatch({ type: ACTIONS.DELETE_DRIVER, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await driverService.delete(id)
      if (result.success) {
        // Clear driver cache to ensure fresh data
        cacheUtils.clearDrivers()
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.ADD_DRIVER, payload: originalDriver })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.ADD_DRIVER, payload: originalDriver })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.drivers])

  // Service actions
  const addService = useCallback(async (serviceData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticService = {
      id: `temp-${Date.now()}`,
      ...serviceData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    dispatch({ type: ACTIONS.ADD_SERVICE, payload: optimisticService })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await serviceService.create(serviceData)
      if (result.success) {
        // Remove optimistic update and add real data
        dispatch({ type: ACTIONS.DELETE_SERVICE, payload: optimisticService.id })
        dispatch({ type: ACTIONS.ADD_SERVICE, payload: result.data })
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.DELETE_SERVICE, payload: optimisticService.id })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.DELETE_SERVICE, payload: optimisticService.id })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateService = useCallback(async (id, serviceData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const optimisticService = { id, ...serviceData, updated_at: new Date().toISOString() }
    const originalService = state.services.find(s => s.id === id)
    
    dispatch({ type: ACTIONS.UPDATE_SERVICE, payload: optimisticService })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await serviceService.update(id, serviceData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_SERVICE, payload: result.data })
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.UPDATE_SERVICE, payload: originalService })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.UPDATE_SERVICE, payload: originalService })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.services])

  const deleteService = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const originalService = state.services.find(s => s.id === id)
    dispatch({ type: ACTIONS.DELETE_SERVICE, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await serviceService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        // Rollback optimistic update
        dispatch({ type: ACTIONS.ADD_SERVICE, payload: originalService })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      // Rollback optimistic update
      dispatch({ type: ACTIONS.ADD_SERVICE, payload: originalService })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.services])

  // Payment actions
  const addPayment = useCallback(async (workEntryId, paymentData) => {
    dispatch({ type: ACTIONS.ADD_PAYMENT, payload: { workEntryId, payment: paymentData } })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await paymentService.create(workEntryId, paymentData)
      if (result.success) {
        // Refresh all data after successful payment
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    }
  }, [])

  const addPaymentOffer = useCallback(async (workEntryId, offerData) => {
    dispatch({ type: ACTIONS.ADD_PAYMENT_OFFER, payload: { workEntryId, offer: offerData } })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await paymentOfferService.create(workEntryId, offerData)
      if (result.success) {
        // Refresh all data after successful payment offer
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    }
  }, [])

  const updatePaymentStatus = useCallback(async (id, paymentStatus) => {
    const originalEntry = state.workEntries.find(w => w.id === id)
    const optimisticEntry = { ...originalEntry, payment_status: paymentStatus }
    
    dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: optimisticEntry })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await workEntryService.updatePaymentStatus(id, paymentStatus)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: result.data })
        // Refresh all data after successful payment status update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: originalEntry })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_WORK_ENTRY, payload: originalEntry })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    }
  }, [state.workEntries])

  // Fuel entry actions
  const addFuelEntry = useCallback(async (fuelData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await fuelService.create(fuelData)
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_FUEL_ENTRY, payload: result.data })
        // Clear fuel entries cache to ensure fresh data
        cacheUtils.clearFuelEntries()
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateFuelEntry = useCallback(async (id, fuelData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    const originalFuelEntry = state.fuelEntries.find(f => f.id === id)
    const optimisticFuelEntry = { ...originalFuelEntry, ...fuelData }
    
    dispatch({ type: ACTIONS.UPDATE_FUEL_ENTRY, payload: optimisticFuelEntry })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await fuelService.update(id, fuelData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_FUEL_ENTRY, payload: result.data })
        // Clear fuel entries cache to ensure fresh data
        cacheUtils.clearFuelEntries()
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_FUEL_ENTRY, payload: originalFuelEntry })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_FUEL_ENTRY, payload: originalFuelEntry })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.fuelEntries])

  const deleteFuelEntry = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const originalFuelEntry = state.fuelEntries.find(f => f.id === id)
    dispatch({ type: ACTIONS.DELETE_FUEL_ENTRY, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await fuelService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.ADD_FUEL_ENTRY, payload: originalFuelEntry })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.ADD_FUEL_ENTRY, payload: originalFuelEntry })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.fuelEntries])

  // Rental actions
  const addRental = useCallback(async (rentalData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await rentalService.create(rentalData)
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_RENTAL, payload: result.data })
        // Clear rentals cache to ensure fresh data
        cacheUtils.clearRentals()
        // Refresh all data after successful creation
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [])

  const updateRental = useCallback(async (id, rentalData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    const originalRental = state.rentals.find(r => r.id === id)
    const optimisticRental = { ...originalRental, ...rentalData }
    
    dispatch({ type: ACTIONS.UPDATE_RENTAL, payload: optimisticRental })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await rentalService.update(id, rentalData)
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_RENTAL, payload: result.data })
        // Clear rentals cache to ensure fresh data
        cacheUtils.clearRentals()
        // Refresh all data after successful update
        await refreshAllData()
        return { success: true, data: result.data }
      } else {
        dispatch({ type: ACTIONS.UPDATE_RENTAL, payload: originalRental })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.UPDATE_RENTAL, payload: originalRental })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.rentals])

  const deleteRental = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    const originalRental = state.rentals.find(r => r.id === id)
    dispatch({ type: ACTIONS.DELETE_RENTAL, payload: id })
    dispatch({ type: ACTIONS.CLEAR_ERROR })

    try {
      const result = await rentalService.delete(id)
      if (result.success) {
        // Refresh all data after successful deletion
        await refreshAllData()
        return { success: true }
      } else {
        dispatch({ type: ACTIONS.ADD_RENTAL, payload: originalRental })
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error })
        return { success: false, error: result.error }
      }
    } catch (error) {
      dispatch({ type: ACTIONS.ADD_RENTAL, payload: originalRental })
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message })
      return { success: false, error: error.message }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false })
    }
  }, [state.rentals])

  const value = useMemo(() => ({
    state: memoizedState,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    assignVehicle,
    unassignVehicle,
    addWorkEntry,
    updateWorkEntry,
    deleteWorkEntry,
    addDriver,
    updateDriver,
    deleteDriver,
    addService,
    updateService,
    deleteService,
    addFuelEntry,
    updateFuelEntry,
    deleteFuelEntry,
    addRental,
    updateRental,
    deleteRental,
    addPayment,
    addPaymentOffer,
    updatePaymentStatus,
    loadInitialData,
    refreshAllData,
    refreshData: () => loadInitialData(true), // Force refresh function
    refreshWorkEntries: async () => {
      const result = await workEntryService.getAll(true)
      if (result.success) {
        dispatch({ type: ACTIONS.SET_WORK_ENTRIES, payload: result.data })
      }
    }
  }), [
    memoizedState,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    assignVehicle,
    unassignVehicle,
    addWorkEntry,
    updateWorkEntry,
    deleteWorkEntry,
    addDriver,
    updateDriver,
    deleteDriver,
    addService,
    updateService,
    deleteService,
    addFuelEntry,
    updateFuelEntry,
    deleteFuelEntry,
    addRental,
    updateRental,
    deleteRental,
    addPayment,
    addPaymentOffer,
    updatePaymentStatus,
    loadInitialData,
  ])

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}

// Custom hook to use the data context
export function useData() {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}
